import React, { useEffect, useState } from "react";
import { makeStyles } from "@mui/styles";
import { TextField, Button, InputAdornment } from "@mui/material";
import Colors from "../../theme/colors.json";
import { useSelector } from "react-redux";
import DeleteIcon from "@mui/icons-material/Delete";
import ConfirmationDialog from "../../components/ConfirmationDialog";
import {
  CONFIRMATION_CONTENT,
  CONFIRMATION_TITLE,
  USER_GROUPS,
} from "../../constants/constants";

const useStyles = makeStyles((theme) => ({
  brokingCompanytable: {
    width: "100%",
    borderCollapse: "collapse",
    border: `1px solid ${Colors.BORDER}`,
    marginBottom: "20px",
    "& th, & td": {
      border: `1px solid ${Colors.BORDER}`,
      padding: "8px",
      textAlign: "left",
    },
    background: Colors.WHITE_BACKGROUND,
    borderRadius: "0.6rem",
  },
  brokingCompanytableHeading: {
    color: theme.palette.secondary.main,
    fontSize: theme.fontSizes.MEDIUM,
    fontWeight: 600,
    // background: theme.palette.primary.main,
  },
  tableTextFieldStyles: {
    fontSize: theme.fontSizes.SMALL,
  },
  validationContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
  },
  validationAction: {
    pointerEvents: "none",
  },
  brokingCompanyTableHeadingCell: {
    fontSize: theme.fontSizes.SMALL,
    fontWeight: 600,
  },
  commonBtnStyles: {
    background: Colors.WHITE_BACKGROUND,
    textTransform: "capitalize",
  },
}));

const BrokingCompanyDetails = ({
  brokingCompanyDetails,
  setBrokingCompanyDetails,
}) => {
  const { proposalDataRedux } = useSelector((state: any) => ({
    proposalDataRedux: state.proposalData,
  }));
  const { userGroup } = useSelector((state: any) => ({
    userGroup: state.userGroup,
  }));
  const classes = useStyles();
  const [tableData, setTableData] = useState(
    proposalDataRedux?.formData?.policyManagementAndCustomerCare
      ?.brokingCompanyDetails?.brokingCompanyDetails || [
      {
        id: Math.random(),
        brokerType: "Exclusive Broker / Lead Broker",
        brokingCompanyName: sessionStorage.getItem("realm") || "",
        sharePercentage: 100,
        premiumAmountBeforeTax:
          proposalDataRedux?.formData?.policyManagementAndCustomerCare
            ?.prePolicyPlacementData?.netPremiumPaid || 0,
      },
    ]
  );

  const [sharePercentage, setSharePercentage] = useState(0);
  const [premiumAmountBeforeTax, setPremiumAmountBeforeTax] = useState(0);
  const [validation, setValidation] = useState(
    proposalDataRedux?.formData?.policyManagementAndCustomerCare
      ?.brokingCompanyDetails?.validation || false
  );

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const handleYes = () => {
    setConfirmDialogOpen(false);
    setValidation(true);
  };

  const handleNo = () => {
    setConfirmDialogOpen(false);
  };

  const addRow = () => {
    const newRow = {
      id: Math.random(),
      brokerType: "",
      brokingCompanyName: "",
      sharePercentage: 0,
      premiumAmountBeforeTax: 0,
    };
    setTableData((prevData) => [...prevData, newRow]);
  };

  const deleteRow = (id) => {
    setTableData((prevData) => prevData.filter((row) => row.id !== id));
  };

  const handleInputChange = (id, field, value) => {
    setTableData((prevData) =>
      prevData.map((row) =>
        row.id === id
          ? {
              ...row,
              [field]: value,
              ["premiumAmountBeforeTax"]:
                ((parseFloat(value) || 0) *
                  (proposalDataRedux?.formData?.policyManagementAndCustomerCare
                    ?.prePolicyPlacementData?.netPremiumPaid || 0)) /
                100,
            }
          : row
      )
    );
  };

  const calculateTotal = (field) => {
    return tableData.reduce((total, row) => {
      const value = parseFloat(row[field]) || 0;
      return total + value;
    }, 0);
  };

  useEffect(() => {
    setSharePercentage(calculateTotal("sharePercentage"));
    setPremiumAmountBeforeTax(calculateTotal("premiumAmountBeforeTax"));
    let data = {
      brokingCompanyDetails: tableData,
      totalSharePercentage: calculateTotal("sharePercentage"),
      totalPremiumAmountBeforeTax: calculateTotal("premiumAmountBeforeTax"),
      validation: validation,
    };
    setBrokingCompanyDetails(data);
  }, [tableData, validation]);

  return (
    <div>
      <ConfirmationDialog
        open={confirmDialogOpen}
        setOpen={setConfirmDialogOpen}
        title={CONFIRMATION_TITLE}
        content={CONFIRMATION_CONTENT}
        handleYes={handleYes}
        handleNo={handleNo}
      />
      <table
        className={`${classes.brokingCompanytable} ${
          validation ? classes.validationAction : ""
        }`}
      >
        <thead>
          <tr>
            <th colSpan={5} className={classes.brokingCompanytableHeading}>
              Broking Company Details
            </th>
          </tr>
          <tr>
            <th className={classes.brokingCompanyTableHeadingCell}>
              Broker Type
            </th>
            <th className={classes.brokingCompanyTableHeadingCell}>
              Broking Company Name
            </th>
            <th className={classes.brokingCompanyTableHeadingCell}>
              % of Share
            </th>
            <th className={classes.brokingCompanyTableHeadingCell}>
              Premium Amount before Tax
            </th>
            <th className={classes.brokingCompanyTableHeadingCell}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {tableData.map((row, index) => (
            <tr key={row.id}>
              <td>
                <TextField
                  type="text"
                  value={row.brokerType}
                  onChange={(e) =>
                    handleInputChange(row.id, "brokerType", e.target.value)
                  }
                  InputProps={{
                    className: classes.tableTextFieldStyles,
                  }}
                  size="small"
                />
              </td>
              <td>
                <TextField
                  type="text"
                  value={row.brokingCompanyName}
                  name="brokingCompanyName"
                  onChange={(e) =>
                    handleInputChange(row.id, e.target.name, e.target.value)
                  }
                  InputProps={{
                    className: classes.tableTextFieldStyles,
                  }}
                  size="small"
                />
              </td>
              <td>
                <TextField
                  type="number"
                  name="sharePercentage"
                  value={row.sharePercentage}
                  onChange={(e) =>
                    handleInputChange(row.id, e.target.name, e.target.value)
                  }
                  size="small"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">%</InputAdornment>
                    ),
                    className: classes.tableTextFieldStyles,
                  }}
                />
              </td>
              <td>
                <TextField
                  type="number"
                  name="premiumAmountBeforeTax"
                  value={row.premiumAmountBeforeTax}
                  disabled
                  // onChange={(e) =>
                  //   handleInputChange(row.id, e.target.name, e.target.value)
                  // }
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">₹</InputAdornment>
                    ),
                    className: classes.tableTextFieldStyles,
                  }}
                />
              </td>
              <td>
                {tableData.length > 1 && (
                  <Button
                    onClick={() => deleteRow(row.id)}
                    color="secondary"
                    size="small"
                  >
                    <DeleteIcon />
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={2}>Total</td>
            <td>
              <TextField
                type="number"
                value={sharePercentage}
                disabled
                size="small"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">%</InputAdornment>
                  ),
                  className: classes.tableTextFieldStyles,
                }}
              />
            </td>
            <td>
              <TextField
                type="number"
                value={premiumAmountBeforeTax}
                disabled
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">₹</InputAdornment>
                  ),
                  className: classes.tableTextFieldStyles,
                }}
              />
            </td>
          </tr>
        </tfoot>
      </table>

      <div className={classes.validationContainer}>
        <Button
          className={classes.commonBtnStyles}
          variant="outlined"
          color="primary"
          onClick={addRow}
          size="small"
          disabled={validation === true ? true : false}
        >
          Add New Row
        </Button>
        <Button
          className={classes.commonBtnStyles}
          variant="outlined"
          size="small"
          color="primary"
          onClick={() => setConfirmDialogOpen(true)}
          disabled={validation === true ? true : false}
        >
          Validation
        </Button>
        {userGroup === USER_GROUPS.ADMIN ? (
          <Button
            className={classes.commonBtnStyles}
            onClick={() => setValidation(false)}
            variant="outlined"
            size="small"
            disabled={!validation}
          >
            Revalidation
          </Button>
        ) : null}
      </div>
    </div>
  );
};

export default BrokingCompanyDetails;
